import { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { TrendingUp, TrendingDown } from "lucide-react";
import { fmtCurrency } from "@/lib/format";

const RANGES = [
  { key: "day", label: "Day", days: 1 },
  { key: "week", label: "Week", days: 7 },
  { key: "month", label: "Month", days: 30 },
  { key: "year", label: "Year", days: 365 },
];

export function TrendChart({ snapshots, getValue, color = "#9DB7C5", currency = "EUR", testid = "trend-chart" }) {
  const [range, setRange] = useState("month");

  const data = useMemo(() => {
    const days = RANGES.find((r) => r.key === range).days;
    const cutoff = Date.now() - days * 86400000;
    return (snapshots || [])
      .filter((s) => new Date(s.timestamp).getTime() >= cutoff)
      .map((s) => ({ t: new Date(s.timestamp).getTime(), value: getValue(s) }))
      .filter((p) => p.value != null);
  }, [snapshots, range, getValue]);

  const delta = data.length >= 2 ? data[data.length - 1].value - data[0].value : 0;
  const up = delta >= 0;

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex gap-1 bg-secondary/60 rounded-full p-1">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              data-testid={`${testid}-range-${r.key}`}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors duration-200 ${
                range === r.key ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        {data.length >= 2 && (
          <span
            className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${
              up ? "text-emerald-700 bg-emerald-50" : "text-rose-700 bg-rose-50"
            }`}
            data-testid={`${testid}-delta`}
          >
            {up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
            {up ? "+" : ""}
            {fmtCurrency(delta, currency)}
          </span>
        )}
      </div>
      {data.length === 0 ? (
        <div className="h-[220px] flex items-center justify-center text-sm text-muted-foreground" data-testid={`${testid}-empty`}>
          No history for this range yet.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <XAxis
              dataKey="t"
              type="number"
              domain={["dataMin", "dataMax"]}
              scale="time"
              tickFormatter={(t) => new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              tick={{ fontSize: 11, fill: "#8C8C8C" }}
              stroke="rgba(0,0,0,0.1)"
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#8C8C8C" }}
              stroke="rgba(0,0,0,0.1)"
              tickFormatter={(v) => new Intl.NumberFormat(undefined, { notation: "compact" }).format(v)}
              width={52}
            />
            <Tooltip
              formatter={(v) => fmtCurrency(v, currency)}
              labelFormatter={(t) => new Date(t).toLocaleString()}
              contentStyle={{ borderRadius: 8, border: "1px solid rgba(0,0,0,0.08)", fontSize: 12 }}
            />
            <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2.5} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

export function MetricChart({ data, color = "#8FB9B2", unit = "", testid = "metric-chart" }) {
  const [range, setRange] = useState("month");

  const points = useMemo(() => {
    const days = RANGES.find((r) => r.key === range).days;
    const cutoff = Date.now() - days * 86400000;
    return (data || [])
      .filter((s) => new Date(s.timestamp).getTime() >= cutoff)
      .map((s) => ({ t: new Date(s.timestamp).getTime(), value: s.value }))
      .filter((p) => p.value != null);
  }, [data, range]);

  const fmt = (v) => `${new Intl.NumberFormat().format(v)}${unit ? " " + unit : ""}`;
  const delta = points.length >= 2 ? points[points.length - 1].value - points[0].value : 0;
  const up = delta >= 0;

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex gap-1 bg-secondary/60 rounded-full p-1">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              data-testid={`${testid}-range-${r.key}`}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors duration-200 ${
                range === r.key ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        {points.length >= 2 && (
          <span
            className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${
              up ? "text-emerald-700 bg-emerald-50" : "text-rose-700 bg-rose-50"
            }`}
            data-testid={`${testid}-delta`}
          >
            {up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
            {up ? "+" : ""}
            {fmt(Math.round(delta * 10) / 10)}
          </span>
        )}
      </div>
      {points.length === 0 ? (
        <div className="h-[200px] flex items-center justify-center text-sm text-muted-foreground" data-testid={`${testid}-empty`}>
          No readings for this range yet.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={points} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <XAxis
              dataKey="t"
              type="number"
              domain={["dataMin", "dataMax"]}
              scale="time"
              tickFormatter={(t) => new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              tick={{ fontSize: 11, fill: "#8C8C8C" }}
              stroke="rgba(0,0,0,0.1)"
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#8C8C8C" }}
              stroke="rgba(0,0,0,0.1)"
              domain={["auto", "auto"]}
              width={44}
            />
            <Tooltip
              formatter={(v) => fmt(v)}
              labelFormatter={(t) => new Date(t).toLocaleString()}
              contentStyle={{ borderRadius: 8, border: "1px solid rgba(0,0,0,0.08)", fontSize: 12 }}
            />
            <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2.5} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

export function AllocationPie({ data, currency = "EUR", testid = "allocation-pie" }) {
  const filtered = (data || []).filter((d) => d.value > 0);
  if (filtered.length === 0) {
    return (
      <div className="h-[240px] flex items-center justify-center text-sm text-muted-foreground" data-testid={`${testid}-empty`}>
        Nothing to allocate yet.
      </div>
    );
  }
  return (
    <div data-testid={testid}>
      <ResponsiveContainer width="100%" height={240}>
        <PieChart>
          <Pie data={filtered} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
            {filtered.map((d) => (
              <Cell key={d.name} fill={d.color} stroke="#fff" strokeWidth={2} />
            ))}
          </Pie>
          <Tooltip formatter={(v) => fmtCurrency(v, currency)} contentStyle={{ borderRadius: 8, border: "1px solid rgba(0,0,0,0.08)", fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}
